/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import {
  Observable,
  catchError,
  combineLatestWith,
  concatMap,
  from,
  interval,
  mergeMap,
  of,
  reduce,
  switchMap,
  takeWhile,
} from 'rxjs';
import { EntityFileResponseItem } from './Entities';
import { FileStoreServiceConfiguration } from './FileStore.service.config';
import {
  ChangePermissionsResponse,
  DeleteDirectoryResponse,
  DeleteFileResponse,
  ExtractArchiveResponse,
  GetStatusResponse,
  ListDirectoriesResponse,
  ListFilesResponse,
  ListObjectsResponse,
  MakeDirectoryResponse,
  PatchDirectoryResponse,
  PatchFileResponse,
  RemoveItemsResponse,
} from './FileStore.service.defs';
import {
  ChangePermissionsDto,
  DeleteDirectoryDto,
  DeleteFileDto,
  ExtractArchiveDto,
  ListDirectoriesDto,
  ListObjectsDto,
  MakeDirectoryDto,
  PatchDirectoryDto,
  PatchFileDto,
  RemoveItemsDto,
  UploadFileDto,
} from './FileStore.service.dto';
import { mapDirectoryToExplorerItem, mapFileToExplorerItem } from './Mappers';

export class FileStoreService extends BaseService {
  public constructor(configuration: FileStoreServiceConfiguration) {
    super(configuration);
  }

  protected serviceHeaders = (): Headers => {
    const baseHeaders = new Headers({ 'Content-Type': 'application/json' });
    return baseHeaders;
  };

  getV2ServiceUrl(): string | null {
    const baseUrl = this.getServiceUrlByTenant('file-storage');
    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/api/v2`;
  }

  createFileURL(fileId?: string): string | null {
    const baseUrl = this.getV2ServiceUrl();
    if (!baseUrl) {
      return null;
    }

    return fileId ? `${baseUrl}/file/${fileId}` : `${baseUrl}/file`;
  }

  createDirectoryURL(directoryId?: string): string | null {
    const baseUrl = this.getV2ServiceUrl();
    if (!baseUrl) {
      return null;
    }

    return directoryId ? `${baseUrl}/directory/${directoryId}` : `${baseUrl}/directory`;
  }

  createStatusURL(executionId: string): string | null {
    const baseUrl = this.getV2ServiceUrl();
    if (!baseUrl) {
      return null;
    }

    return `${baseUrl}/status/${executionId}`;
  }

  createDownloadURL(fileId: string): string | null {
    const fileUrl = this.createFileURL(fileId);
    if (!fileUrl) {
      return null;
    }

    return `${fileUrl}/download`;
  }

  uploadFiles(files: Array<UploadFileDto>): Observable<EntityFileResponseItem | null> {
    const requestUrl = this.createFileURL();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return from(files).pipe(
      concatMap((fileDto) => {
        return this.httpService
          .post<UploadFileDto, HttpResponse>(requestUrl, {
            ...this.getSessionRequestOptions(),
            body: fileDto,
          })
          .pipe(
            switchMap((value: HttpResponse): Observable<EntityFileResponseItem> => {
              return this._handleResponseError<EntityFileResponseItem>(value);
            }),
          );
      }),
    );
  }

  changePermissions(dto: ChangePermissionsDto): Observable<ChangePermissionsResponse | null> {
    const { access, cascadeAccess, items } = dto;

    return from(items).pipe(
      mergeMap((item) => {
        const { id, type } = item;

        switch (type) {
          case 'DIRECTORY':
            return this.patchDirectory({ access, cascadeAccess, directoryId: id });
          case 'OBJECT':
          default:
            return this.patchFile({ access, fileId: id });
        }
      }),
      reduce(
        (acc: ChangePermissionsResponse, statusResponse: GetStatusResponse | null) => {
          if (statusResponse === null) {
            throw new Error('Undefined tenant');
          }

          if (statusResponse.data) {
            acc.errors += statusResponse.data.errors;
            acc.successes += statusResponse.data.successes;
          }

          return acc;
        },
        { errors: 0, successes: 0 },
      ),
      catchError((error) => {
        // eslint-disable-next-line no-console
        console.error('Request error:', error);
        return of(null);
      }),
    );
  }

  patchFile(dto: PatchFileDto): Observable<GetStatusResponse | null> {
    const { fileId, ...requestBody } = dto;

    const requestUrl = this.createFileURL(fileId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .patch<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: requestBody,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<PatchFileResponse> => {
          return this._handleResponseError<PatchFileResponse>(value);
        }),
        switchMap((): Observable<GetStatusResponse> => {
          return of({ data: { errors: 0, successes: 1 }, requestId: fileId, status: 'SUCCEED' });
        }),
      );
  }

  patchDirectory(dto: PatchDirectoryDto): Observable<GetStatusResponse | null> {
    const { directoryId, ...requestBody } = dto;

    const requestUrl = this.createDirectoryURL(directoryId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .patch<unknown, HttpResponse<PatchDirectoryResponse>>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: requestBody,
      })
      .pipe(
        switchMap((res) => {
          const pollingInterval = 5000;
          const requestId = res.body?.requestId;
          if (!requestId) {
            throw new Error('Request ID is missing from the response');
          }

          const executionId = `patch-${requestId}`;
          const statusUrl = this.createStatusURL(executionId) as string;

          return interval(pollingInterval).pipe(
            switchMap(() =>
              this.httpService
                .get<unknown, HttpResponse<GetStatusResponse>>(statusUrl, {
                  ...this.getSessionRequestOptions(),
                })
                .pipe(
                  switchMap((value: HttpResponse): Observable<GetStatusResponse> => {
                    return this._handleResponseError<GetStatusResponse>(value);
                  }),
                ),
            ),
            takeWhile((response) => response.status === 'PENDING', true),
          );
        }),
      );
  }

  makeDirectory(dto: MakeDirectoryDto): Observable<MakeDirectoryResponse | null> {
    const requestUrl = this.createDirectoryURL();

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const request = this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body: dto,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<MakeDirectoryResponse> => {
          return this._handleResponseError<MakeDirectoryResponse>(value);
        }),
      );
    return request;
  }

  sortDataBySource = <T extends { source: string }>(data: Array<T>): Array<T> => {
    return data.sort((a, b) => {
      const sourceA = a.source.toLowerCase();
      const sourceB = b.source.toLowerCase();

      if (sourceA < sourceB) {
        return -1;
      }
      if (sourceA > sourceB) {
        return 1;
      }
      return 0;
    });
  };

  listObjects(dto: ListObjectsDto): Observable<ListObjectsResponse | null> {
    const { source } = dto;

    const queryParams: Array<string> = [];

    if (source) {
      queryParams.push(`${encodeURIComponent('source')}=${source}`);
    }

    const baseDirectoryUrl = this.createDirectoryURL();
    const baseFileUrl = this.createFileURL();

    if (!baseDirectoryUrl || !baseFileUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestDirectoriesUrl = this.appendQueryParamsToUrl(queryParams, baseDirectoryUrl);
    const requestFilesUrl = this.appendQueryParamsToUrl(queryParams, baseFileUrl);

    const directoryObservable = this.httpService
      .get<unknown, HttpResponse>(requestDirectoriesUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<ListDirectoriesResponse> => {
          return this._handleResponseError<ListDirectoriesResponse>(value);
        }),
      );

    const fileObservable = this.httpService
      .get<unknown, HttpResponse>(requestFilesUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<ListFilesResponse> => {
          return this._handleResponseError<ListFilesResponse>(value);
        }),
      );

    return directoryObservable.pipe(combineLatestWith(fileObservable)).pipe(
      switchMap(([directories, files]) => {
        const sortedDirectories = this.sortDataBySource(directories);
        const sortedFiles = this.sortDataBySource(files);

        const explorerDirectoryItems = sortedDirectories.map((directory) => mapDirectoryToExplorerItem(directory));
        const explorerFileItems = sortedFiles.map((file) => mapFileToExplorerItem(file));

        return of({ data: [...explorerDirectoryItems, ...explorerFileItems] });
      }),
    );
  }

  listDirectories(dto: ListDirectoriesDto): Observable<ListObjectsResponse | null> {
    const { source } = dto;

    const queryParams: Array<string> = [];

    if (source) {
      queryParams.push(`${encodeURIComponent('source')}=${source}`);
    }

    const baseUrl = this.createDirectoryURL();

    if (!baseUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseUrl);

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<ListDirectoriesResponse> => {
          return this._handleResponseError<ListDirectoriesResponse>(value);
        }),
        switchMap((directories: ListDirectoriesResponse) => {
          return of({ data: directories.map((directory) => mapDirectoryToExplorerItem(directory)) });
        }),
      );
  }

  removeItems(dto: RemoveItemsDto): Observable<RemoveItemsResponse | null> {
    return from(dto.items).pipe(
      mergeMap((item) => {
        const { id, type } = item;

        switch (type) {
          case 'DIRECTORY':
            return this.deleteDirectory({ directoryId: id });
          case 'OBJECT':
          default:
            return this.deleteFile({ fileId: id });
        }
      }),
      reduce(
        (acc: RemoveItemsResponse, statusResponse: GetStatusResponse | null) => {
          if (statusResponse === null) {
            throw new Error('Undefined tenant');
          }

          if (statusResponse.data) {
            acc.errors += statusResponse.data.errors;
            acc.successes += statusResponse.data.successes;
          }

          return acc;
        },
        { errors: 0, successes: 0 },
      ),
      catchError((error) => {
        // eslint-disable-next-line no-console
        console.error('Request error:', error);
        return of(null);
      }),
    );
  }

  deleteFile(dto: DeleteFileDto): Observable<GetStatusResponse | null> {
    const { fileId } = dto;

    const requestUrl = this.createFileURL(fileId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse<DeleteFileResponse>>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((res) => {
          const pollingInterval = 3000;
          const requestId = res.body?.requestId;
          if (!requestId) {
            throw new Error('Request ID is missing from the response');
          }

          const executionId = `delete-${requestId}`;
          const statusUrl = this.createStatusURL(executionId) as string;

          return interval(pollingInterval).pipe(
            switchMap(() =>
              this.httpService
                .get<unknown, HttpResponse<GetStatusResponse>>(statusUrl, {
                  ...this.getSessionRequestOptions(),
                })
                .pipe(
                  switchMap((value: HttpResponse): Observable<GetStatusResponse> => {
                    return this._handleResponseError<GetStatusResponse>(value);
                  }),
                ),
            ),
            takeWhile((response) => response.status === 'PENDING', true),
          );
        }),
      );
  }

  deleteDirectory(dto: DeleteDirectoryDto): Observable<GetStatusResponse | null> {
    const { directoryId } = dto;

    const requestUrl = this.createDirectoryURL(directoryId);

    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse<DeleteDirectoryResponse>>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((res) => {
          const pollingInterval = 5000;
          const requestId = res.body?.requestId;
          if (!requestId) {
            throw new Error('Request ID is missing from the response');
          }

          const executionId = `delete-${requestId}`;
          const statusUrl = this.createStatusURL(executionId) as string;

          return interval(pollingInterval).pipe(
            switchMap(() =>
              this.httpService
                .get<unknown, HttpResponse<GetStatusResponse>>(statusUrl, {
                  ...this.getSessionRequestOptions(),
                })
                .pipe(
                  switchMap((value: HttpResponse): Observable<GetStatusResponse> => {
                    return this._handleResponseError<GetStatusResponse>(value);
                  }),
                ),
            ),
            takeWhile((response) => response.status === 'PENDING', true),
          );
        }),
      );
  }

  extractArchive(dto: ExtractArchiveDto): Observable<GetStatusResponse | null> {
    const { fileId, ...body } = dto;

    const serviceUrl = this.createFileURL(fileId);

    if (!serviceUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = `${serviceUrl}/extract`;

    return this.httpService
      .post<Omit<ExtractArchiveDto, 'fileId'>, HttpResponse<ExtractArchiveResponse>>(requestUrl, {
        ...this.getSessionRequestOptions(),
        body,
      })
      .pipe(
        switchMap((res) => {
          const pollingInterval = 5000;
          const requestId = res.body?.requestId;
          if (!requestId) {
            throw new Error('Request ID is missing from the response');
          }

          const executionId = `extract-${requestId}`;
          const statusUrl = this.createStatusURL(executionId) as string;

          return interval(pollingInterval).pipe(
            switchMap(() =>
              this.httpService
                .get<unknown, HttpResponse<GetStatusResponse>>(statusUrl, {
                  ...this.getSessionRequestOptions(),
                })
                .pipe(
                  switchMap((value: HttpResponse): Observable<GetStatusResponse> => {
                    return this._handleResponseError<GetStatusResponse>(value);
                  }),
                ),
            ),
            takeWhile((response) => response.status === 'PENDING', true),
          );
        }),
      );
  }
}
